import { AxiosRequest } from "../ApiCall";
import { configEnv } from "../constant";
import type { TaskFilters } from "../../pages/TaskAndGoals/types/task.types";

const BASE = `${configEnv.baseUrl}/tasks`;

export const taskApi = {
    /**
     * Tasks a student's assessment or evaluation scores triggered.
     * sourceType is "attempt" or "evaluation"; an evaluation needs a studentId.
     */
    getRecommendations: async (
        sourceType: "attempt" | "evaluation",
        sourceId: string,
        header: any,
        studentId?: string
    ) => {
        const query = studentId ? `?studentId=${studentId}` : "";
        const res = await AxiosRequest(
            "GET",
            `${BASE}/recommendations/${sourceType}/${sourceId}${query}`,
            null,
            header
        );
        return res.data;
    },

    /** Ask the model to write task suggestions for this student and source. */
    generateRecommendationAI: async (
        sourceType: "attempt" | "evaluation",
        sourceId: string,
        header: any,
        studentId?: string
    ) => {
        const query = studentId ? `?studentId=${studentId}` : "";
        const res = await AxiosRequest(
            "POST",
            `${BASE}/recommendations/${sourceType}/${sourceId}/generate-ai${query}`,
            {},
            header,
            // Generation waits on the model.
            { timeout: 120000 }
        );
        return res.data;
    },

    getAll: async (orgId: string, header: any, filters?: TaskFilters) => {
        const params = new URLSearchParams();
        if (filters?.department) params.set("department", filters.department);
        if (filters?.unit) params.set("unit", filters.unit);
        if (filters?.startDate) params.set("startDate", filters.startDate);
        if (filters?.endDate) params.set("endDate", filters.endDate);
        const url = `${BASE}/get-all-feedback-by-organization/${orgId}?${params.toString()}`;
        const res = await AxiosRequest("GET", url, null, header);
        return res.data;
    },

    getGroupedByStatus: async (orgId: string, header: any) => {
        const res = await AxiosRequest("GET", `${BASE}/board/${orgId}`, null, header);
        return res.data;
    },

    getByUser: async (userId: string, header: any) => {
        const res = await AxiosRequest("GET", `${BASE}/user-feedbacks/${userId}`, null, header);
        return res.data;
    },

    getById: async (id: string, header: any) => {
        const res = await AxiosRequest("GET", `${BASE}/${id}`, null, header);
        return res.data;
    },

    getByShortLink: async (shortLink: string) => {
        const res = await AxiosRequest("GET", `${BASE}/short-link/${shortLink}`, null, {});
        return res.data;
    },

    create: async (body: Record<string, any>, header: any) => {
        const res = await AxiosRequest("POST", `${BASE}/create-feedback`, body, header);
        return res.data;
    },

    updateField: async (id: string, field: string, value: any, header: any) => {
        const res = await AxiosRequest("PATCH", `${BASE}/${id}/field`, { field, value }, header);
        return res.data;
    },

    update: async (id: string, body: Record<string, any>, header: any) => {
        const res = await AxiosRequest("PUT", `${BASE}/${id}`, body, header);
        return res.data;
    },

    bulkUpdate: async (taskIds: string[], updates: Record<string, any>, header: any) => {
        const res = await AxiosRequest("POST", `${BASE}/bulk-update`, { taskIds, updates }, header);
        return res.data;
    },

    delete: async (id: string, header: any) => {
        const res = await AxiosRequest("DELETE", `${BASE}/${id}`, null, header);
        return res.data;
    },

    addComment: async (taskId: string, text: string, header: any) => {
        const res = await AxiosRequest("POST", `${BASE}/${taskId}/comments`, { text }, header);
        return res.data;
    },

    getComments: async (taskId: string, page: number, header: any) => {
        const res = await AxiosRequest("GET", `${BASE}/${taskId}/comments?page=${page}&limit=20`, null, header);
        return res.data;
    },

    getActivityLog: async (taskId: string, header: any) => {
        const res = await AxiosRequest("GET", `${BASE}/${taskId}/activity`, null, header);
        return res.data;
    },

    reorder: async (taskId: string, newSortOrder: number, status: string | undefined, header: any) => {
        const res = await AxiosRequest("PATCH", `${BASE}/${taskId}/reorder`, { newSortOrder, status }, header);
        return res.data;
    },

    rate: async (feedbackId: string, rating: number, remarks: string, header: any) => {
        const res = await AxiosRequest("POST", `${BASE}/rate`, { feedbackId, rating, remarks }, header);
        return res.data;
    },

    generateShortLink: async (taskId: string, header: any) => {
        const res = await AxiosRequest("POST", `${BASE}/${taskId}/generate-short-link`, {}, header);
        return res.data;
    },
};
